import { useState, Fragment } from 'react'
import { PHENO_CODES } from '../../hooks/usePhenomenaData'
import { buildStationRows, rowHeaderBlocks, rowHierarchyDepth, rowGroupStarts } from './shared/groupRows'
import { stationLabelLines } from './shared/StationLabelFields'
import { PhenomenaDayGrid } from '../reports/shared/PhenomenaDayGrid'

// Styl jako MlyGrid — tenké čáry, gray-400 jen kolem hlavičky a sticky sloupce.
const th = 'border-b border-b-gray-400 border-r border-r-gray-200 px-1.5 py-1 text-xs font-normal text-center bg-gray-50 whitespace-nowrap'
const cb = 'border-b border-r border-gray-200 px-1.5 py-1 text-xs text-center tabular-nums'

function countBg(n, max) {
  if (!n || max <= 0) return null
  const t = Math.min(1, n / max)
  return `rgba(37,99,235,${(0.12 + t * 0.55).toFixed(2)})`
}

// Záložka "Přehled" produktu grp-jevy — matice řádky = stanice, sloupce = jevy, buňka = počet dní
// v měsíci, kdy jev na stanici nastal. Klik na řádek rozbalí denní detail (jev × den) té stanice.
// Viz doc/plans/station-group-monthly-yearly-phenomena.md sekce 5a.
export function GroupJevyOverviewGrid({ nodes, stationsByGhId, countMap, dayMap, defsMap, dayKeys, labelFields, orientation = 'normal' }) {
  const rows = buildStationRows(nodes, stationsByGhId)
  const depth = rowHierarchyDepth(nodes)
  const [expanded, setExpanded] = useState(null)

  if (orientation === 'transposed') {
    return (
      <GroupJevyOverviewGridTransposed
        rows={rows} depth={depth} stationsByGhId={stationsByGhId}
        countMap={countMap} defsMap={defsMap} labelFields={labelFields}
      />
    )
  }

  // Popisek hierarchie se opakuje na každém řádku jen tam, kde se oproti předchozímu řádku změní
  // (rowSpan by se rozbil při rozbalení detailního řádku uprostřed skupiny).
  const branchLabel = (row, d, ri) => {
    const node = row.branch[d]
    if (!node) return null
    if (ri > 0 && rows[ri - 1].branch[d] === node) return ''
    return node.label
  }

  let max = 0
  for (const counts of countMap.values()) for (const n of counts.values()) max = Math.max(max, n)

  const groupStarts = depth >= 1 ? rowGroupStarts(rows, 0) : new Set()
  const topRule = ri => (groupStarts.has(ri) ? 'border-t border-t-gray-400' : '')

  return (
    <div className="h-full overflow-hidden border border-gray-200">
      <div className="h-full overflow-auto">
        <table className="border-separate border-spacing-0 border-r-2 border-r-gray-400 border-b-2 border-b-gray-400 text-xs">
          <colgroup>
            {Array.from({ length: depth }, (_, i) => <col key={`h${i}`} className="w-28" />)}
            <col className="w-48" />
          </colgroup>
          <thead className="sticky top-0 z-20">
            <tr>
              <th
                colSpan={depth + 1}
                className="sticky left-0 z-30 bg-gray-50 border-t-2 border-t-gray-400 border-l-2 border-l-gray-400 border-b border-b-gray-400 border-r border-r-gray-400 px-3 py-1 text-left text-gray-600 font-semibold whitespace-nowrap"
              >
                Stanice <span className="text-gray-400 text-[10px]">(dny s jevem)</span>
              </th>
              {PHENO_CODES.map(code => {
                const def = defsMap.get(code)
                return (
                  <th key={code} className={`${th} border-t-2 border-t-gray-400`} title={def?.name ?? code}>
                    <div className="flex flex-col items-center gap-0.5">
                      {def?.svg && <span className="inline-block w-4 h-4" dangerouslySetInnerHTML={{ __html: def.svg }} />}
                      <span className="text-gray-500">{code}</span>
                    </div>
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, ri) => {
              const station = stationsByGhId.get(row.gh)
              const counts = countMap.get(row.gh)
              const isOpen = expanded === row.rowKey
              return (
                <Fragment key={row.rowKey}>
                  <tr className="group hover:bg-slate-50">
                    {Array.from({ length: depth }, (_, d) => (
                      <td
                        key={d}
                        className={`sticky z-10 bg-gray-100 border-b border-r border-gray-200 px-2 py-0.5 text-left align-top text-[11px] font-medium text-gray-600 whitespace-nowrap ${d === 0 ? 'border-l-2 border-l-gray-400' : ''} ${topRule(ri)}`}
                        style={{ left: `${d * 7}rem` }}
                      >
                        {branchLabel(row, d, ri)}
                      </td>
                    ))}
                    <td
                      className={`sticky z-10 bg-white group-hover:bg-slate-50 border-b border-r border-r-gray-400 px-3 py-0.5 text-left whitespace-nowrap cursor-pointer text-blue-700 hover:underline ${depth === 0 ? 'border-l-2 border-l-gray-400' : 'border-l border-l-gray-400'} ${topRule(ri)}`}
                      style={{ left: `${depth * 7}rem` }}
                      onClick={() => setExpanded(isOpen ? null : row.rowKey)}
                    >
                      {stationLabelLines(station, labelFields, row.gh).map((line, li) => (
                        <div key={li} className={li > 0 ? 'text-[10px] text-blue-400' : ''}>
                          {li === 0 ? `${isOpen ? '▾ ' : '▸ '}${line}` : line}
                        </div>
                      ))}
                    </td>
                    {PHENO_CODES.map(code => {
                      const n = counts?.get(code) ?? 0
                      const bg = countBg(n, max)
                      return (
                        <td
                          key={code}
                          style={bg ? { backgroundColor: bg } : undefined}
                          className={`${cb} ${topRule(ri)} ${n === 0 ? 'text-gray-300 bg-white group-hover:bg-slate-50' : 'text-gray-800'}`}
                        >
                          {n || '·'}
                        </td>
                      )
                    })}
                  </tr>
                  {isOpen && (
                    <tr>
                      <td colSpan={depth + 1 + PHENO_CODES.length} className="border-b border-b-gray-400 bg-gray-50 p-3">
                        <PhenomenaDayGrid dayKeys={dayKeys} defsMap={defsMap} dayMap={dayMap.get(row.gh) ?? new Map()} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              )
            })}
            {rows.length === 0 && (
              <tr>
                <td colSpan={depth + 1 + PHENO_CODES.length} className="px-4 py-6 text-center text-gray-400">
                  Žádné stanice
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ── Transponovaná varianta ─────────────────────────────────────────────────────
// řádky = jevy, sloupce = stanice (stromová hlavička nahoře). Bez rozbalování denního detailu.
function GroupJevyOverviewGridTransposed({ rows, depth, stationsByGhId, countMap, defsMap, labelFields }) {
  const headerRows = Array.from({ length: depth }, (_, d) => rowHeaderBlocks(rows, d))
  const groupStarts = depth >= 1 ? rowGroupStarts(rows, 0) : new Set()
  const leftRule = ci => (groupStarts.has(ci) ? 'border-l border-l-gray-400' : '')

  let max = 0
  for (const counts of countMap.values()) for (const n of counts.values()) max = Math.max(max, n)

  return (
    <div className="h-full overflow-hidden border border-gray-200">
      <div className="h-full overflow-auto">
        <table className="border-separate border-spacing-0 border-r-2 border-r-gray-400 border-b-2 border-b-gray-400 text-xs">
          <thead className="sticky top-0 z-20">
            {headerRows.map((blocks, d) => (
              <tr key={`h${d}`}>
                {d === 0 && (
                  <th rowSpan={depth + 1} className="sticky left-0 z-30 bg-gray-50 border-t-2 border-t-gray-400 border-l-2 border-l-gray-400 border-b border-b-gray-400 border-r border-r-gray-400 px-3 py-1 text-left align-bottom text-gray-600 font-semibold whitespace-nowrap">
                    Jev <span className="text-gray-400 text-[10px]">(dny s jevem)</span>
                  </th>
                )}
                {blocks.map(block => (
                  <th key={`${d}-${block.start}`} colSpan={block.span} className={`${th} border-t-2 border-t-gray-400 text-gray-500 ${leftRule(block.start)}`}>
                    {block.label}
                  </th>
                ))}
              </tr>
            ))}
            <tr>
              {depth === 0 && (
                <th className="sticky left-0 z-30 bg-gray-50 border-t-2 border-t-gray-400 border-l-2 border-l-gray-400 border-b border-b-gray-400 border-r border-r-gray-400 px-3 py-1 text-left text-gray-600 font-semibold whitespace-nowrap">
                  Jev <span className="text-gray-400 text-[10px]">(dny s jevem)</span>
                </th>
              )}
              {rows.map((row, ci) => {
                const station = stationsByGhId.get(row.gh)
                return (
                  <th key={row.rowKey} className={`${th} text-gray-600 font-medium ${depth === 0 ? 'border-t-2 border-t-gray-400' : ''} ${leftRule(ci)}`}>
                    {stationLabelLines(station, labelFields, row.gh).map((line, li) => (
                      <div key={li} className={li > 0 ? 'text-[10px] text-gray-400 font-normal' : ''}>{line}</div>
                    ))}
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {PHENO_CODES.map(code => {
              const def = defsMap.get(code)
              return (
                <tr key={code} className="group hover:bg-slate-50">
                  <td className="sticky left-0 z-10 bg-white group-hover:bg-slate-50 border-b border-l-2 border-l-gray-400 border-r border-r-gray-400 px-2 py-1 text-left whitespace-nowrap" title={def?.name ?? code}>
                    <span className="inline-flex items-center gap-1.5">
                      {def?.svg && <span className="inline-block w-4 h-4" dangerouslySetInnerHTML={{ __html: def.svg }} />}
                      <span className="font-semibold text-gray-600">{code}</span>
                    </span>
                  </td>
                  {rows.map((row, ci) => {
                    const n = countMap.get(row.gh)?.get(code) ?? 0
                    const bg = countBg(n, max)
                    return (
                      <td
                        key={row.rowKey}
                        style={bg ? { backgroundColor: bg } : undefined}
                        className={`${cb} ${leftRule(ci)} ${n === 0 ? 'text-gray-300 bg-white group-hover:bg-slate-50' : 'text-gray-800'}`}
                      >
                        {n || '·'}
                      </td>
                    )
                  })}
                </tr>
              )
            })}
            {rows.length === 0 && (
              <tr>
                <td colSpan={depth + 1 + PHENO_CODES.length} className="px-4 py-6 text-center text-gray-400">
                  Žádné stanice
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
